import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sheet, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { logError } from "@/lib/log";

const Flag = ({ ok, children }) => (
  <span className={`text-xs px-2 py-0.5 rounded-full border ${ok ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-amber-50 border-amber-300 text-amber-800"}`}>{children}</span>
);

export const FinanceSyncPanel = () => {
  const [st, setSt] = useState(null);
  const [mapping, setMapping] = useState(null);
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/finance-sync/status").then((r) => setSt(r.data))
    .catch((e) => logError("Finance sync status failed", e));
  useEffect(() => { load(); }, []);

  const run = async (what, fn) => {
    setBusy(what);
    try { await fn(); load(); } finally { setBusy(""); }
  };
  const test = () => run("test", async () => {
    const r = await api.post("/finance-sync/test", {});
    (r.data.ok ? toast.success : toast.error)(r.data.message);
  });
  const preview = () => run("mapping", async () => {
    try {
      const r = await api.get("/finance-sync/mapping");
      if (r.data.ok === false) { toast.info(r.data.message); setMapping(null); return; }
      setMapping(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memuat mapping"); }
  });
  const syncNow = () => run("sync", async () => {
    try {
      const r = await api.post("/finance-sync/sync-now", {});
      toast.success(`${r.data.synced} order disinkron${r.data.failed.length ? `, ${r.data.failed.length} gagal` : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  });
  const retry = () => run("retry", async () => {
    const r = await api.post("/finance-sync/retry-failed", {});
    toast.success(`${r.data.synced}/${r.data.retried} berhasil disinkron ulang`);
  });
  const syncAssets = () => run("assets", async () => {
    try {
      const r = await api.post("/finance-sync/assets", {});
      toast.success(`${r.data.units_updated} unit diperbarui di 04_Assets`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  });
  const showLogs = () => run("logs", async () => {
    const r = await api.get("/finance-sync/logs", { params: { limit: 20 } });
    setLogs(r.data);
  });

  if (!st) return null;
  return (
    <section className="bg-white border border-black/10 rounded-lg p-5" data-testid="finance-sync-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-base font-semibold flex items-center gap-2"><Sheet size={16} /> Finance Sync — Google Sheets</h2>
        <div className="flex flex-wrap gap-2">
          <button data-testid="fs-test" disabled={!!busy} onClick={test} className="h-9 px-3 rounded-md border border-black/15 text-xs hover:border-lm-red disabled:opacity-50">Test Connection</button>
          <button data-testid="fs-mapping" disabled={!!busy} onClick={preview} className="h-9 px-3 rounded-md border border-black/15 text-xs hover:border-lm-red disabled:opacity-50">Preview Mapping</button>
          <button data-testid="fs-sync" disabled={!!busy} onClick={syncNow} className="h-9 px-3 rounded-md bg-ink text-cream text-xs hover:bg-lm-red disabled:opacity-50 flex items-center gap-1.5">
            {busy === "sync" && <RefreshCw size={12} className="animate-spin" />} Sync Now
          </button>
          <button data-testid="fs-retry" disabled={!!busy} onClick={retry} className="h-9 px-3 rounded-md border border-black/15 text-xs hover:border-lm-red disabled:opacity-50">Retry Failed</button>
          <button data-testid="fs-assets" disabled={!!busy} onClick={syncAssets} className="h-9 px-3 rounded-md border border-black/15 text-xs hover:border-lm-red disabled:opacity-50">Sync 04_Assets</button>
          <button data-testid="fs-logs" disabled={!!busy} onClick={showLogs} className="h-9 px-3 rounded-md border border-black/15 text-xs hover:border-lm-red disabled:opacity-50">Lihat Log</button>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Flag ok={st.configured}>{st.configured ? "Terhubung" : "Belum dikonfigurasi"}</Flag>
        <span className="text-xs text-ink-soft">Synced: {st.synced} · Pending: {st.pending} · Gagal: {st.failed}</span>
        {st.spreadsheet_title && <span className="text-xs text-ink-soft">Spreadsheet: {st.spreadsheet_title}</span>}
      </div>

      {!st.configured && (
        <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="fs-setup-note">
          Kirim <b>Service Account JSON</b> Google Cloud (Sheets API aktif) supaya saya isi ke server. Setelah itu
          share spreadsheet <b>LapakMahjong.id — Finance &amp; Rental Master</b> ke email service account tersebut sebagai <b>Editor</b>.
        </div>
      )}
      {st.service_account_email && (
        <div className="mt-3 text-xs">
          Service account: <code data-testid="fs-sa-email" className="bg-cream-dark px-2 py-1 rounded">{st.service_account_email}</code>
          <span className="text-ink-soft"> — share spreadsheet ke email ini sebagai Editor.</span>
        </div>
      )}
      <div className="mt-2 text-xs text-ink-soft">
        Spreadsheet ID: <code className="break-all">{st.spreadsheet_id || "—"}</code>
        {st.last_sync_at && <> · Sync terakhir: {st.last_sync_at.slice(0, 16).replace("T", " ")}</>}
      </div>
      {st.last_error && <div className="mt-2 text-xs text-red-600" data-testid="fs-error">Error terakhir: {st.last_error}</div>}

      {mapping && (
        <div className="mt-4 max-h-64 overflow-y-auto border border-black/10 rounded-md" data-testid="fs-mapping-table">
          <table className="w-full text-xs">
            <thead className="bg-cream-dark text-left uppercase tracking-widest text-ink-soft">
              <tr><th className="p-2">Kolom</th><th className="p-2">Header</th><th className="p-2">Mode</th></tr>
            </thead>
            <tbody className="divide-y divide-black/5">
              {mapping.columns.map((c) => (
                <tr key={c.column}>
                  <td className="p-2">{c.column}</td>
                  <td className="p-2">{c.header}</td>
                  <td className={`p-2 ${c.mode === "Writable" ? "text-emerald-700" : c.mode.startsWith("Formula") ? "text-amber-700" : "text-ink-soft"}`}>{c.mode}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {logs.length > 0 && (
        <div className="mt-4 max-h-52 overflow-y-auto divide-y divide-black/5 text-xs" data-testid="fs-logs">
          {logs.map((l) => (
            <div key={l.id} className="py-1.5 flex flex-wrap gap-x-3">
              <span className={l.ok ? "text-emerald-700" : "text-red-600"}>{l.ok ? "OK" : "GAGAL"}</span>
              <span>{l.sheet} row {l.row}</span>
              <span className="text-ink-soft">{l.action}</span>
              <span className="text-ink-soft">{(l.at || "").slice(0, 16).replace("T", " ")}</span>
              {l.error && <span className="text-red-600">{l.error}</span>}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
